import { supabase } from "@/integrations/supabase/client";

export type BillingCustomer = {
  id: string;
  email: string | null;
  name: string | null;
  created: number;
  currency: string | null;
};

export type BillingSubscription = {
  id: string;
  status: string;
  current_period_start: number;
  current_period_end: number;
  trial_start: number | null;
  trial_end: number | null;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  items: Array<{
    id: string;
    price_id: string;
    product_id: string;
    quantity: number;
    product_name?: string;
    price_amount?: number;
    price_currency?: string;
    price_interval?: string;
  }>;
  discount?: {
    coupon: {
      id: string;
      name: string | null;
      percent_off: number | null;
      amount_off: number | null;
      duration: string;
    };
  };
};

export type BillingInvoice = {
  id: string;
  number: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string | null;
  created: number;
  period_start: number | null;
  period_end: number | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  subscription: string | null;
  payment_intent?: {
    id: string;
    status: string;
    last_payment_error?: {
      code: string;
      message: string;
    };
  };
};

export type BillingPaymentMethod = {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  billing_details?: {
    name: string | null;
    email: string | null;
  };
  created: number;
};

export type BillingTimelineEvent = {
  timestamp: number;
  type: string;
  description: string;
  data?: any;
};

export type BillingOverview = {
  customer: BillingCustomer | null;
  subscriptions: BillingSubscription[];
  invoices: BillingInvoice[];
  payment_methods: BillingPaymentMethod[];
  upcoming_invoice: {
    amount_due: number;
    currency: string;
    period_start: number;
    period_end: number;
    lines: Array<{
      description: string | null;
      amount: number;
      quantity: number;
      proration: boolean;
    }>;
  } | null;
  timeline: BillingTimelineEvent[];
  diagnostics: {
    parity: {
      stored_status: string | null;
      effective_status: string | null;
      stored_plan_id: string | null;
      effective_plan_id: string | null;
      stored_subscription_id: string | null;
      effective_subscription_id: string | null;
      stored_period_end: string | null;
      effective_period_end: number | null;
      mismatches: string[];
    };
    last_webhook_timestamp: string | null;
    recent_stripe_events: Array<{
      id: string;
      type: string;
      created: number;
    }>;
  };
  request_id: string;
  fetched_at: string;
};

class SuperAdminBillingService {
  async getBillingOverview(
    organizationId: string,
    requestingOrgId: string,
    options?: {
      invoiceLimit?: number;
      cursor?: string;
    },
  ): Promise<{ data?: BillingOverview; error?: Error }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "super-admin-billing-overview",
        {
          body: {
            organizationId,
            requestingOrgId,
            invoiceLimit: options?.invoiceLimit,
            cursor: options?.cursor,
          },
        },
      );

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error("Failed to get billing overview:", error);
      return { error: error as Error };
    }
  }

  formatCurrency(amount: number, currency: string): string {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    });
    return formatter.format(amount / 100);
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  formatDateTime(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  getStatusBadgeVariant(
    status: string,
  ): "default" | "secondary" | "destructive" | "outline" {
    const statusMap: Record<
      string,
      "default" | "secondary" | "destructive" | "outline"
    > = {
      active: "default",
      trialing: "secondary",
      past_due: "destructive",
      canceled: "outline",
      incomplete: "destructive",
      incomplete_expired: "destructive",
      unpaid: "destructive",
      paused: "secondary",
      paid: "default",
      open: "secondary",
      draft: "outline",
      void: "outline",
      uncollectible: "destructive",
    };
    return statusMap[status?.toLowerCase()] || "outline";
  }

  exportToCSV(overview: BillingOverview): string {
    const lines: string[] = [];

    // Customer info
    lines.push("Customer Information");
    lines.push("ID,Email,Name,Created,Currency");
    if (overview.customer) {
      lines.push(
        `"${overview.customer.id}","${overview.customer.email || ""}","${overview.customer.name || ""}","${this.formatDate(overview.customer.created)}","${overview.customer.currency || ""}"`,
      );
    }
    lines.push("");

    // Subscriptions
    lines.push("Subscriptions");
    lines.push("ID,Status,Period Start,Period End,Trial End,Plan,Amount");
    overview.subscriptions.forEach((sub) => {
      const item = sub.items[0];
      lines.push(
        `"${sub.id}","${sub.status}","${this.formatDate(sub.current_period_start)}","${this.formatDate(sub.current_period_end)}","${sub.trial_end ? this.formatDate(sub.trial_end) : ""}","${item?.product_name || ""}","${item?.price_amount ? this.formatCurrency(item.price_amount, item.price_currency || "usd") : ""}"`,
      );
    });
    lines.push("");

    // Invoices
    lines.push("Invoices");
    lines.push("Number,Date,Amount Due,Amount Paid,Status,Invoice URL");
    overview.invoices.forEach((inv) => {
      lines.push(
        `"${inv.number || inv.id}","${this.formatDate(inv.created)}","${this.formatCurrency(inv.amount_due, inv.currency)}","${this.formatCurrency(inv.amount_paid, inv.currency)}","${inv.status || ""}","${inv.hosted_invoice_url || ""}"`,
      );
    });

    return lines.join("\n");
  }

  exportToJSON(overview: BillingOverview): string {
    return JSON.stringify(overview, null, 2);
  }

  downloadFile(content: string, filename: string, type: "csv" | "json") {
    const mimeType = type === "csv" ? "text/csv" : "application/json";
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export const superAdminBillingService = new SuperAdminBillingService();
